import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation } from 'urql';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { Q_PROJECTS, M_CREATE_DRAFT, M_CREATE_TEMPLATE, M_SCHEDULE_DRAFT, Q_MEDIA, M_ADD_MEDIA, M_CREATE_MEDIA_UPLOAD, M_COMPLETE_MEDIA_UPLOAD, Q_CONTENT_ITEMS } from '@/lib/queries';
import { useUi } from '@/stores/ui.store';
import { useWorkspace } from '@/stores/workspace.store';
import { useAuth } from '@/auth/AuthContext';
import { PlatformPreview } from '@/components/PlatformPreview';
import { characterLimits, postTypesByPlatform, defaultPostType, validateLinkedInPost, validateXPost, xCaptionMax, X_POLL_MIN_OPTIONS, X_POLL_MAX_OPTIONS, X_POLL_OPTION_MAX, type XPoll, type XValidationInput, type Platform } from '@contenthub/shared';

const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

/** Per-platform character limit, sourced from contenthub-shared (not hard-coded). */
const charLimit = (p: string) => characterLimits[p as Platform] ?? 5000;
/**
 * Effective caption cap. X is connection-derived (long-posts capability) via the
 * shared `xCaptionMax` helper — not wired yet, so we pass longPosts=false (280)
 * and the counter switches automatically when the capability lands.
 */
const limitFor = (p: string) => (p === 'twitter' ? xCaptionMax(false) : charLimit(p));

const PLATFORMS = [
  { id: 'linkedin',  label: 'LinkedIn',     glyph: 'in', bg: 'bg-blue-600' },
  { id: 'twitter',   label: 'X / Twitter',  glyph: '𝕏', bg: 'bg-stone-900' },
  { id: 'facebook',  label: 'Facebook',     glyph: 'f', bg: 'bg-blue-500' },
  { id: 'instagram', label: 'Instagram',    glyph: '◷', bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400' },
  { id: 'tiktok',    label: 'TikTok',       glyph: '♪', bg: 'bg-black' },
  { id: 'youtube',   label: 'YouTube',      glyph: '▶', bg: 'bg-red-600' },
  { id: 'threads',   label: 'Threads',      glyph: '@', bg: 'bg-stone-900' },
  { id: 'pinterest', label: 'Pinterest',    glyph: 'P', bg: 'bg-red-500' },
];
const TONES = ['professional', 'casual', 'bold', 'witty', 'inspirational', 'educational'];

/**
 * Presentation only — icon/title/sub per `${platform}:${postType}`. The *set*
 * and *order* of post types per platform (and the default) come from
 * contenthub-shared (`postTypesByPlatform` / `defaultPostType`); this map just
 * supplies the labels. Falls back to a generic label if shared adds a type.
 */
const POST_TYPE_LABELS: Record<string, { icon: string; title: string; sub: string }> = {
  'linkedin:text':          { icon: '✎', title: 'Text post',     sub: 'Text only' },
  'linkedin:single-image':  { icon: '▢', title: 'Single image',  sub: 'Image + text' },
  'linkedin:multi-image':   { icon: '▦', title: 'Multi-image',   sub: '2–9 images' },
  'linkedin:document':      { icon: '▤', title: 'Document',      sub: 'PDF slides (swipe)' },
  'linkedin:video':         { icon: '▶', title: 'Video',         sub: 'Native video' },
  'linkedin:link':          { icon: '⌘', title: 'Link share',    sub: 'URL + preview card' },
  'twitter:text':           { icon: '✎', title: 'Tweet',                sub: 'Text only' },
  'twitter:single-image':   { icon: '▢', title: 'Tweet + image',        sub: 'One image' },
  'twitter:multi-image':    { icon: '▦', title: 'Tweet + images (up to 4)', sub: 'Up to 4 images' },
  'twitter:video':          { icon: '▶', title: 'Video',                sub: 'Upload video' },
  'twitter:gif':            { icon: '◉', title: 'GIF',                  sub: 'Animated GIF' },
  'twitter:poll':           { icon: '▤', title: 'Poll',                 sub: '2–4 options' },
  'twitter:link':           { icon: '⌘', title: 'Link',                 sub: 'URL + card' },
  'twitter:thread':         { icon: '≡', title: 'Thread',               sub: 'Connected tweets' },
  'facebook:text':          { icon: '✎', title: 'Text post',     sub: 'Text only' },
  'facebook:single-image':  { icon: '▢', title: 'Single image',  sub: 'Image + text' },
  'facebook:multi-image':   { icon: '⊞', title: 'Multi-image',   sub: 'Multiple images' },
  'facebook:link':          { icon: '⌘', title: 'Link preview',  sub: 'URL with card' },
  'instagram:feed-single':  { icon: '▢', title: 'Feed post',     sub: 'Single image' },
  'instagram:feed-carousel':{ icon: '⊞', title: 'Carousel',      sub: 'Up to 10 images' },
  'instagram:reels':        { icon: '▶', title: 'Reels',         sub: 'Vertical video' },
  'instagram:stories':      { icon: '◉', title: 'Story',         sub: '24h ephemeral' },
  'tiktok:video':           { icon: '▶', title: 'Video',         sub: 'Standard upload' },
  'tiktok:photo':           { icon: '▢', title: 'Photo carousel',sub: 'Up to 35 photos' },
  'tiktok:live':            { icon: '◉', title: 'Go live',       sub: 'Live stream link' },
  'youtube:video':          { icon: '▶', title: 'Video',         sub: 'Long-form upload' },
  'youtube:short':          { icon: '▤', title: 'Short',         sub: 'Vertical < 60s' },
  'youtube:community':      { icon: '✎', title: 'Community',     sub: 'Text + poll' },
  'threads:text':           { icon: '✎', title: 'Thread',        sub: 'Text only' },
  'threads:image':          { icon: '▢', title: 'Image thread',  sub: 'Text + image' },
  'threads:video':          { icon: '▶', title: 'Video thread',  sub: 'Text + video' },
  'pinterest:pin':          { icon: '▢', title: 'Pin',           sub: 'Image + link' },
  'pinterest:video-pin':    { icon: '▶', title: 'Video Pin',     sub: 'Video + link' },
  'pinterest:idea-pin':     { icon: '⊞', title: 'Idea Pin',      sub: 'Multi-page story' },
};

/** Build the post-type cards for a platform — IDs/order from shared, labels here. */
const cardsFor = (platform: string) =>
  (postTypesByPlatform[platform as Platform] ?? postTypesByPlatform.linkedin).map((id) => ({
    id,
    ...(POST_TYPE_LABELS[`${platform}:${id}`] ?? { icon: '▢', title: id, sub: '' }),
  }));
const COMMON_EMOJIS = ['🚀','✨','🎉','💡','📈','🎯','❤️','👏','🔥','⚡','🤝','✅','💪','🌟','📊','💼','🙌','📣','🧠','🌍'];
const COMMON_HASHTAGS = ['#marketing','#contentcreation','#socialmedia','#leadership','#startup','#growth','#ai','#productivity','#community','#brand'];
const TEMPLATE_CATEGORIES = ['general', 'promo', 'engagement', 'educational', 'announcement'];

/** Common words skipped when deriving hashtags from caption text. */
const HASHTAG_STOPWORDS = new Set([
  'about','after','again','against','their','there','these','those','where','which','while','would','could','should',
  'because','before','being','between','through','during','having','other','under','until','within','your','yours',
  'with','that','this','from','they','them','then','than','what','when','will','have','here','more','most','some',
  'such','only','also','into','over','very','just','like','make','made','were','been','does','done','each','many',
]);
/**
 * Cheap, local, no-API hashtag suggestions derived from the caption: words
 * >4 chars, minus stopwords, deduped, title-cased, `#`-prefixed, capped at 5.
 */
const deriveHashtags = (caption: string): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of caption.toLowerCase().match(/[a-z][a-z'-]{4,}/g) ?? []) {
    const w = raw.replace(/['-]/g, '');
    if (w.length <= 4 || HASHTAG_STOPWORDS.has(w) || seen.has(w)) continue;
    seen.add(w);
    out.push('#' + w[0].toUpperCase() + w.slice(1));
    if (out.length >= 5) break;
  }
  return out;
};

export default function CreateRoute() {
  const nav = useNavigate();
  const location = useLocation();
  const { toast } = useUi();
  const { workspace_name, credits } = useWorkspace();
  // AI actions cost credits; gate them at zero balance (same balance the top
  // bar's ⚡ shows, from the workspace store).
  const noCredits = credits <= 0;
  const { user } = useAuth();

  const [projects] = useQuery({ query: Q_PROJECTS });
  const [mediaQ, refetchMedia] = useQuery({ query: Q_MEDIA, variables: { limit: 200 } });
  // Kept content items for the "Compose from a library item" picker. Reuses the
  // Content library route's query (workspace-scoped via context), filtered to
  // kept items, relevance-ordered — same as the library shows.
  const [sourceItemsQ] = useQuery({
    query: Q_CONTENT_ITEMS,
    variables: { triageStatus: 'kept', orderBy: 'relevance', limit: 100 },
  });
  const [, createDraft] = useMutation(M_CREATE_DRAFT);
  const [, scheduleDraft] = useMutation(M_SCHEDULE_DRAFT);
  const [, createTemplate] = useMutation(M_CREATE_TEMPLATE);
  const [, addMedia] = useMutation(M_ADD_MEDIA);
  const [, createMediaUpload] = useMutation(M_CREATE_MEDIA_UPLOAD);
  const [, completeMediaUpload] = useMutation(M_COMPLETE_MEDIA_UPLOAD);

  const mediaLibrary: any[] = mediaQ.data?.mediaAssets ?? [];
  const mediaById = useMemo(() => Object.fromEntries(mediaLibrary.map((m) => [m.id, m])), [mediaLibrary]);

  const project = (projects.data?.projects?.find((p:any)=>p.id===useWorkspace.getState().project_id) ?? projects.data?.projects?.[0]);
  const connections: any[] = project?.connections ?? [];
  const connsFor = (p: string) => connections.filter((c) => c.platform === p && c.status === 'connected');
  // Per-platform chosen account (connection id). LinkedIn can expose a personal
  // profile + multiple Company Pages; this picks which one we author as. Defaults
  // to the first connected account when none is explicitly chosen.
  const [accountByPlatform, setAccountByPlatform] = useState<Record<string, string>>({});
  const connFor = (p: string) => {
    const list = connsFor(p);
    return list.find((c) => c.id === accountByPlatform[p]) ?? list[0];
  };
  // Only platforms the user actually has a live connection for — drives both
  // the "Publishing to" chip list and the tabs row below.
  const connectedPlatforms = PLATFORMS.filter((p) => connFor(p.id));
  const firstConnected = connectedPlatforms[0]?.id ?? '';

  const [selected, setSelected] = useState<string[]>(() => firstConnected ? [firstConnected] : []);
  const [active, setActive] = useState<string>(firstConnected || 'linkedin');
  // If connections load AFTER first render (common — Q_PROJECTS is async),
  // adopt the first connected platform so the page isn't stuck on "linkedin"
  // when the user only has Twitter.
  useEffect(() => {
    if (selected.length === 0 && connectedPlatforms.length > 0) {
      setSelected([connectedPlatforms[0].id]);
      setActive(connectedPlatforms[0].id);
    }
  }, [connectedPlatforms.length]);
  // Per-platform post type — each tab keeps its own.
  const [postType, setPostType] = useState<Record<string, string>>({ ...defaultPostType });
  const currentPostType = postType[active] ?? defaultPostType[active as Platform] ?? 'single-image';
  const activeCards = cardsFor(active);
  const setCurrentPostType = (id: string) => setPostType((m) => ({ ...m, [active]: id }));

  const [sharedCaption, setSharedCaption] = useState<string>(() => {
    const t = sessionStorage.getItem('template_body');
    if (t) { sessionStorage.removeItem('template_body'); return t; }
    return '';
  });
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const isOverridden = (p: string) => p in overrides;
  const captionFor = (p: string) => (isOverridden(p) ? overrides[p] : sharedCaption);
  const currentCaption = captionFor(active);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const setCurrentCaption = (val: string) => {
    if (isOverridden(active)) setOverrides((o) => ({ ...o, [active]: val }));
    else setSharedCaption(val);
  };
  const enableOverride = () => {
    if (isOverridden(active)) setOverrides(({ [active]: _, ...rest }) => rest);
    else setOverrides((o) => ({ ...o, [active]: sharedCaption }));
  };
  // Switch to platform `p`, start a per-platform caption override (seeded from
  // the shared caption) so the user can trim a shorter variant, and focus the
  // caption. Used by the over-limit nudge.
  const customizeFor = (p: string) => {
    setActive(p);
    setOverrides((o) => (p in o ? o : { ...o, [p]: sharedCaption }));
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const insertAtCaret = (text: string) => {
    const ta = textareaRef.current;
    if (!ta) { setCurrentCaption(currentCaption + text); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    const next = currentCaption.slice(0, start) + text + currentCaption.slice(end);
    setCurrentCaption(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  // ── @mentions — per-platform structured capture ───────────────────────
  // We have no platform user-search API, so a mention is a handle the author
  // types. It's inserted into the caption AND captured on posts.mentions (the
  // schema's `mentions: string[]`) so adapters can resolve/render it natively.
  const [mentionsByPlatform, setMentionsByPlatform] = useState<Record<string, string[]>>({});
  const currentMentions = mentionsByPlatform[active] ?? [];
  // Normalise a typed handle: strip a leading @ and whitespace.
  const cleanHandle = (raw: string) => raw.trim().replace(/^@+/, '').replace(/\s+/g, '');
  const addMention = () => {
    const h = cleanHandle(mentionInput);
    if (!h) return;
    insertAtCaret(`@${h} `);
    setMentionsByPlatform((m) => ({ ...m, [active]: [...new Set([...(m[active] ?? []), h])] }));
    setMentionInput('');
    setShowMentions(false);
  };
  const removeMention = (h: string) =>
    setMentionsByPlatform((m) => ({ ...m, [active]: (m[active] ?? []).filter((x) => x !== h) }));

  // ── Hashtags — custom + caption-derived suggestions ───────────────────
  // Turn a free-text phrase into a single camel-cased tag word (no spaces/#).
  const toTagWord = (raw: string) =>
    raw.trim().replace(/^#+/, '').split(/\s+/).filter(Boolean)
      .map((w, i) => (i === 0 ? w : w[0].toUpperCase() + w.slice(1))).join('');
  const addCustomHashtag = () => {
    const w = toTagWord(hashtagInput);
    if (!w) return;
    insertAtCaret(` #${w}`);
    setHashtagInput('');
  };

  const [idea, setIdea] = useState(() => {
    const i = sessionStorage.getItem('create_idea');
    if (i) { sessionStorage.removeItem('create_idea'); return i; }
    return '';
  });
  const [sourceItemId, setSourceItemId] = useState<string | null>(() => {
    const s = sessionStorage.getItem('source_item_id');
    if (s) { sessionStorage.removeItem('source_item_id'); return s; }
    return null;
  });
  // Title of the source content item we're composing from (router-state prefill
  // or the in-composer picker), shown in a banner so the user knows the caption
  // was seeded.
  const [prefillTitle, setPrefillTitle] = useState<string | null>(null);

  // Attach a content item as the source: seed the SHARED caption (title + summary
  // + source url, only the parts present) and record id + title. Used identically
  // by the router-state prefill (mount) and the in-composer "Pick from library".
  const attachSource = (src: { contentItemId?: string | null; title?: string | null; summary?: string | null; url?: string | null }) => {
    const parts: string[] = [];
    if (src.title) parts.push(src.title);
    if (src.summary) parts.push('\n\n' + src.summary);
    if (src.url) parts.push('\n\nSource: ' + src.url);
    const seed = parts.join('');
    if (seed) setSharedCaption(seed);
    if (src.contentItemId) setSourceItemId(src.contentItemId);
    if (src.title) setPrefillTitle(src.title);
  };
  // Detach the source link WITHOUT wiping the (possibly edited) caption.
  const detachSource = () => { setPrefillTitle(null); setSourceItemId(null); };

  // Consume router-state prefill once on mount: when "Create posts" is clicked
  // on a Content library / Review queue item, that screen navigates here with
  // { state: { prefill: { contentItemId, title, summary, url } } }.
  useEffect(() => {
    const prefill = (location.state as any)?.prefill;
    if (!prefill) return;
    attachSource(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Source picker modal (compose from / change source) + client-side title filter.
  const [showSourcePicker, setShowSourcePicker] = useState(false);
  const [sourceSearch, setSourceSearch] = useState('');
  const [tone, setTone] = useState('professional');
  const [generating, setGenerating] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showEmoji, setShowEmoji] = useState(false);
  const [showHashtags, setShowHashtags] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  // Draft text inside the popovers (handle to add / custom hashtag word).
  const [mentionInput, setMentionInput] = useState('');
  const [hashtagInput, setHashtagInput] = useState('');
  const [showValidator, setShowValidator] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showAddUrl, setShowAddUrl] = useState(false);
  const [addUrlValue, setAddUrlValue] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);

  // Per-platform media attachments — array of media_asset ids.
  const [mediaByPlatform, setMediaByPlatform] = useState<Record<string, string[]>>({});
  const currentMediaIds = mediaByPlatform[active] ?? [];

  const attachMedia = (id: string) => {
    setMediaByPlatform((m) => ({ ...m, [active]: [...(m[active] ?? []), id].filter((x, i, arr) => arr.indexOf(x) === i) }));
  };
  const removeMedia = (id: string) => {
    setMediaByPlatform((m) => ({ ...m, [active]: (m[active] ?? []).filter((x) => x !== id) }));
  };

  // Real file upload via presigned PUT: createMediaUpload → PUT bytes to S3 →
  // completeMediaUpload (records the asset). Bytes never pass through the API.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const uploadFile = async (file: File) => {
    if (!project?.id) return toast('No project', 'error');
    const contentType = file.type || 'application/octet-stream';
    setUploading(true);
    try {
      const cu = await createMediaUpload({ input: { projectId: project.id, filename: file.name, contentType, sizeBytes: file.size } });
      if (cu.error) throw new Error(cu.error.message);
      const ticket = cu.data?.createMediaUpload;
      if (!ticket?.uploadUrl) throw new Error('no upload URL returned');
      const put = await fetch(ticket.uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!put.ok) throw new Error(`storage rejected upload (${put.status})`);
      const done = await completeMediaUpload({ input: { projectId: project.id, storageKey: ticket.storageKey, contentType, sizeBytes: file.size } });
      if (done.error) throw new Error(done.error.message);
      const id = done.data?.completeMediaUpload?.id;
      if (id) attachMedia(id);
      refetchMedia({ requestPolicy: 'network-only' });
      toast('File uploaded');
    } catch (e: any) {
      toast(`Upload failed: ${e?.message ?? 'error'}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  // Per-image edits (composer-local). NOT persisted at publish time today —
  // real adapters upload the source asset. Used in the composer + preview only.
  const FILTER_CYCLE = ['none', 'grayscale', 'sepia', 'contrast', 'fade'] as const;
  const FILTER_CSS: Record<string, string> = {
    none: 'none',
    grayscale: 'grayscale(1)',
    sepia: 'sepia(0.6) saturate(1.2)',
    contrast: 'contrast(1.3) saturate(1.2)',
    fade: 'brightness(1.1) saturate(0.7) contrast(0.95)',
  };
  type ImageEdit = { rotate: number; filter: string };
  const [imageEdits, setImageEdits] = useState<Record<string, ImageEdit>>({});
  const editFor = (id: string): ImageEdit => imageEdits[id] ?? { rotate: 0, filter: 'none' };
  const rotate90 = (id: string) => setImageEdits((m) => ({ ...m, [id]: { ...editFor(id), rotate: (editFor(id).rotate + 90) % 360 } }));
  const cycleFilter = (id: string) => {
    const cur = editFor(id).filter;
    const next = FILTER_CYCLE[(FILTER_CYCLE.indexOf(cur as any) + 1) % FILTER_CYCLE.length];
    setImageEdits((m) => ({ ...m, [id]: { ...editFor(id), filter: next } }));
  };
  const editStyle = (id: string): React.CSSProperties => {
    const e = editFor(id);
    return { transform: e.rotate ? `rotate(${e.rotate}deg)` : undefined, filter: FILTER_CSS[e.filter] };
  };

  // Per-platform metadata (LinkedIn visibility/comments today). Stored on
  // posts.meta jsonb so platform adapters can read it at publish time.
  type PostMeta = { visibility?: string; comments?: string };
  const [metaByPlatform, setMetaByPlatform] = useState<Record<string, PostMeta>>({
    linkedin: { visibility: 'PUBLIC', comments: 'ALL' },
  });
  const currentMeta = metaByPlatform[active] ?? {};
  const setCurrentMeta = (patch: PostMeta) =>
    setMetaByPlatform((m) => ({ ...m, [active]: { ...(m[active] ?? {}), ...patch } }));

  // Per-platform link URL — only meaningful for the LinkedIn 'link' post type
  // today. Travels to the worker on posts.meta.link_url (read at publish time).
  const [linkUrlByPlatform, setLinkUrlByPlatform] = useState<Record<string, string>>({});
  const currentLinkUrl = linkUrlByPlatform[active] ?? '';
  const setCurrentLinkUrl = (val: string) =>
    setLinkUrlByPlatform((m) => ({ ...m, [active]: val }));

  // ── X / Twitter composer state ────────────────────────────────────────
  // Poll (twitter + postType 'poll'): 2–4 options + duration. Travels to the
  // worker on posts.meta.poll. Default: 2 empty options, 1 day.
  const POLL_DURATIONS: { label: string; minutes: number }[] = [
    { label: '5 minutes', minutes: 5 },
    { label: '1 hour', minutes: 60 },
    { label: '6 hours', minutes: 360 },
    { label: '1 day', minutes: 1440 },
    { label: '7 days', minutes: 10080 },
  ];
  const [pollByPlatform, setPollByPlatform] = useState<Record<string, XPoll>>({});
  const currentPoll = pollByPlatform[active] ?? { options: ['', ''], durationMinutes: 1440 };
  const setCurrentPoll = (patch: Partial<XPoll>) =>
    setPollByPlatform((m) => ({ ...m, [active]: { ...currentPoll, ...patch } }));
  const setPollOption = (i: number, val: string) => {
    const options = [...currentPoll.options];
    options[i] = val;
    setCurrentPoll({ options });
  };
  const addPollOption = () => {
    if (currentPoll.options.length >= X_POLL_MAX_OPTIONS) return;
    setCurrentPoll({ options: [...currentPoll.options, ''] });
  };
  const removePollOption = (i: number) => {
    if (currentPoll.options.length <= X_POLL_MIN_OPTIONS) return;
    setCurrentPoll({ options: currentPoll.options.filter((_, idx) => idx !== i) });
  };

  // Quote / reply target (twitter, any type). Stored on posts.meta as
  // quote_post_id / in_reply_to_id at publish time.
  type XRef = { mode: 'quote' | 'reply' | null; postId: string | null };
  const [xRefByPlatform, setXRefByPlatform] = useState<Record<string, XRef>>({});
  const currentXRef = xRefByPlatform[active] ?? { mode: null, postId: null };
  // Free-text the user pastes; we extract the numeric status id from it.
  const [xRefRawByPlatform, setXRefRawByPlatform] = useState<Record<string, string>>({});
  const currentXRefRaw = xRefRawByPlatform[active] ?? '';
  const parseXPostId = (raw: string): string | null => {
    const t = raw.trim();
    if (!t) return null;
    const m = t.match(/status\/(\d+)/);
    if (m) return m[1];
    if (/^\d+$/.test(t)) return t;
    return null;
  };
  const setXRefRaw = (raw: string) => {
    setXRefRawByPlatform((m) => ({ ...m, [active]: raw }));
    setXRefByPlatform((m) => ({ ...m, [active]: { mode: currentXRef.mode ?? 'quote', postId: parseXPostId(raw) } }));
  };
  const setXRefMode = (mode: 'quote' | 'reply') =>
    setXRefByPlatform((m) => ({ ...m, [active]: { mode, postId: parseXPostId(currentXRefRaw) } }));

  // Thread builder (twitter + postType 'thread'): an ordered list of segments,
  // each with its own text + media. Travels to the worker on posts.meta.thread.
  type ThreadSegment = { text: string; mediaIds: string[] };
  const [threadByPlatform, setThreadByPlatform] = useState<Record<string, ThreadSegment[]>>({});
  const currentThread = threadByPlatform[active] ?? [{ text: '', mediaIds: [] }];
  const setThread = (segs: ThreadSegment[]) =>
    setThreadByPlatform((m) => ({ ...m, [active]: segs }));
  const setSegmentText = (i: number, text: string) =>
    setThread(currentThread.map((s, idx) => (idx === i ? { ...s, text } : s)));
  const addSegment = () => setThread([...currentThread, { text: '', mediaIds: [] }]);
  const removeSegment = (i: number) => {
    if (currentThread.length <= 1) return;
    setThread(currentThread.filter((_, idx) => idx !== i));
  };
  const moveSegment = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= currentThread.length) return;
    const next = [...currentThread];
    [next[i], next[j]] = [next[j], next[i]];
    setThread(next);
  };
  const toggleSegmentMedia = (i: number, id: string) =>
    setThread(currentThread.map((s, idx) => {
      if (idx !== i) return s;
      const has = s.mediaIds.includes(id);
      return { ...s, mediaIds: has ? s.mediaIds.filter((x) => x !== id) : [...s.mediaIds, id] };
    }));
  // Which segment's media picker is open (segment index, or null). Reuses the
  // shared library modal scoped to that segment.
  const [threadMediaPickerFor, setThreadMediaPickerFor] = useState<number | null>(null);

  // ── X connection capabilities ─────────────────────────────────────────
  // Q_PROJECTS now returns connections.capabilities (JSON):
  //   { x_tier: string|null, x_long_posts: boolean }
  // x_long_posts ⇒ long caption cap; x_tier null ⇒ X cannot publish (tier gate).
  // We read from the SELECTED twitter connection (connFor already resolves the
  // per-platform chosen account, defaulting to the first connected one).
  const xCapsFor = (p: string): { x_tier: string | null; x_long_posts: boolean } => {
    const caps = connFor(p)?.capabilities ?? {};
    return { x_tier: caps.x_tier ?? null, x_long_posts: !!caps.x_long_posts };
  };
  const xLongPostsFor = (p: string) => xCapsFor(p).x_long_posts;
  const xTierConfiguredFor = (p: string) => !!xCapsFor(p).x_tier;
  // Effective caption cap: X is capability-derived (long-posts) via the shared
  // helper; everything else uses the module-level limitFor.
  const effLimit = (p: string) => (p === 'twitter' ? xCaptionMax(xLongPostsFor(p)) : limitFor(p));

  // Schedule + template form state
  const [scheduleAt, setScheduleAt] = useState<string>(() => {
    const d = new Date(Date.now() + 30 * 60_000);  // default: +30 min
    return d.toISOString().slice(0, 16);             // for <input type="datetime-local">
  });
  const [tplName, setTplName] = useState('');
  const [tplCategory, setTplCategory] = useState('general');
  const [previewViewport, setPreviewViewport] = useState<'desktop'|'mobile'>('desktop');

  const togglePlatform = (id: string) => {
    setSelected((sel) => {
      const next = sel.includes(id) ? sel.filter((p) => p !== id) : [...sel, id];
      if (!next.includes(active)) setActive(next[0] ?? id);
      if (sel.includes(id) && !next.includes(id)) setOverrides(({ [id]: _, ...rest }) => rest);
      return next;
    });
  };

  const callAi = async (path: string, body: any) => {
    const ws = localStorage.getItem('workspace_id') ?? '';
    const token = localStorage.getItem('access_token') ?? '';
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': ws, Authorization: token ? `Bearer ${token}` : '' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  };

  const generate = async () => {
    if (!project?.id) return toast('No project', 'error');
    if (!idea.trim()) return toast('Describe an idea to generate from', 'warning');
    setGenerating(true);
    try {
      const data = await callAi('/v1/ai/generate', {
        project_id: project.id, source_mode: 'idea', source_idea: idea,
        platforms: [active], tone, length: 'medium',
        options: { hashtags: true, emojis: true, language: 'en' },
      });
      const d = data.drafts?.find((x: any) => x.platform === active) ?? data.drafts?.[0];
      if (d?.caption) { setCurrentCaption(d.caption); toast(`Generated for ${active} (${data.credits_used}⚡)`); }
    } catch (e: any) { toast(`Generation failed: ${e?.message ?? 'error'}`, 'error'); }
    finally { setGenerating(false); }
  };

  const handleAddUrl = async () => {
    if (!project?.id) return toast('No project', 'error');
    if (!addUrlValue.trim()) return toast('Paste an image URL', 'warning');
    const r = await addMedia({ input: { projectId: project.id, url: addUrlValue.trim() } });
    if (r.error) return toast(r.error.message, 'error');
    const id = r.data?.addMediaAsset?.id;
    if (id) attachMedia(id);
    setAddUrlValue('');
    setShowAddUrl(false);
    refetchMedia({ requestPolicy: 'network-only' });
    toast('Image attached');
  };

  const aiGenerateImage = async () => {
    if (!project?.id) return toast('No project', 'error');
    if (!currentCaption.trim()) return toast('Write a caption first — the image is generated from it', 'warning');
    setGeneratingImage(true);
    try {
      const data = await callAi('/v1/ai/image/from-caption', { caption: currentCaption, platform: active });
      if (!data?.url) throw new Error('no image returned');
      const r = await addMedia({ input: { projectId: project.id, url: data.url, altText: `AI-generated for ${active}` } });
      if (r.error) throw new Error(r.error.message);
      const id = r.data?.addMediaAsset?.id;
      if (id) attachMedia(id);
      refetchMedia({ requestPolicy: 'network-only' });
      toast(`Image generated (${data.credits_used}⚡)`);
    } catch (e: any) {
      toast(`Image generation failed: ${e?.message ?? 'error'}`, 'error');
    } finally { setGeneratingImage(false); }
  };

  const aiRewrite = async () => {
    if (!currentCaption.trim()) return toast('Write something first', 'warning');
    setRewriting(true);
    try {
      const data = await callAi('/v1/ai/rewrite', {
        caption: currentCaption, platform: active, tone, length: 'medium',
        options: { hashtags: true, emojis: true, language: 'en' },
      });
      if (data.caption) { setCurrentCaption(data.caption); toast(`Rewritten (${data.credits_used}⚡)`); }
    } catch (e: any) { toast(`Rewrite failed: ${e?.message ?? 'error'}`, 'error'); }
    finally { setRewriting(false); }
  };

  /** Fan out createDraft. Optionally chain scheduleDraft on each. Returns the created post ids. */
  const fanOut = async (scheduleWhen?: Date) => {
    if (!project?.id) { toast('No project', 'error'); return [] as string[]; }
    if (selected.length === 0) { toast('Pick at least one platform', 'warning'); return []; }
    const overLimit = selected.filter((p) => {
      // Threads carry their text in per-segment editors, not the top-level
      // caption — skip the generic empty/over-limit guard for them (the X
      // validator + blockingCount gate per-segment length instead).
      if (p === 'twitter' && (postType[p] ?? defaultPostType.twitter) === 'thread') return false;
      const c = captionFor(p);
      return c.length === 0 || c.length > effLimit(p);
    });
    if (overLimit.length) { toast(`Caption empty or over limit for: ${overLimit.join(', ')}`, 'warning'); return []; }

    setSaving(true);
    const results = await Promise.all(selected.map(async (p) => {
      const conn = connFor(p);
      if (!conn) return { platform: p, ok: false, error: 'no-connection' as const, id: null };
      const pType = postType[p] ?? defaultPostType[p as Platform] ?? 'single-image';
      // Only attach link_url for 'link' post types — the worker reads
      // posts.meta.link_url at publish time to build the share/article card.
      const link = (linkUrlByPlatform[p] ?? '').trim();
      // X-specific meta: poll (poll type) + quote/reply reference (any type).
      // The worker reads posts.meta.{poll,quote_post_id,in_reply_to_id}.
      const xPoll = pollByPlatform[p];
      const xRef = xRefByPlatform[p];
      const isThread = p === 'twitter' && pType === 'thread';
      const segs = threadByPlatform[p] ?? [{ text: '', mediaIds: [] }];
      const meta = {
        ...(metaByPlatform[p] ?? {}),
        ...(pType === 'link' && link ? { link_url: link } : {}),
        ...(p === 'twitter' && pType === 'poll' && xPoll
          ? { poll: { options: xPoll.options.map((o) => o.trim()).filter(Boolean), duration_minutes: xPoll.durationMinutes } }
          : {}),
        ...(p === 'twitter' && xRef?.postId && xRef.mode === 'quote' ? { quote_post_id: xRef.postId } : {}),
        ...(p === 'twitter' && xRef?.postId && xRef.mode === 'reply' ? { in_reply_to_id: xRef.postId } : {}),
        // Thread segments — ordered [{ text, media_ids }]. The worker posts them
        // in order and writes meta.thread_progress as it goes.
        ...(isThread ? { thread: segs.map((s) => ({ text: s.text, media_ids: s.mediaIds })) } : {}),
      };
      const r = await createDraft({
        input: {
          projectId: project.id, connectionId: conn.id, platform: p,
          postType: pType,
          // For threads the top-level caption mirrors the first segment (used by
          // list/preview); media lives per-segment so top-level mediaIds is [].
          caption: isThread ? (segs[0]?.text ?? '') : captionFor(p),
          sourceItemId,
          mediaIds: isThread ? [] : (mediaByPlatform[p] ?? []),
          // Structured @mention handles captured in the composer toolbar. The
          // schema's createDraftInput accepts `mentions: string[]`.
          mentions: mentionsByPlatform[p] ?? [],
          meta,
        },
      });
      const id = r.data?.createDraft?.id ?? null;
      // Optionally schedule it right after
      if (id && scheduleWhen) {
        await scheduleDraft({ id, when: scheduleWhen.toISOString() });
      }
      return { platform: p, ok: !r.error, error: r.error?.message, id };
    }));
    setSaving(false);

    const ok = results.filter((r) => r.ok);
    const failed = results.filter((r) => !r.ok);
    if (ok.length) toast(`${ok.length} ${scheduleWhen ? 'scheduled' : 'draft'}${ok.length > 1 ? 's' : ''} (${ok.map((r) => r.platform).join(', ')})`);
    if (failed.length) toast(`Failed: ${failed.map((r) => `${r.platform} (${r.error})`).join('; ')}`, 'error');
    return ok.map((r) => r.id).filter(Boolean) as string[];
  };

  const saveDrafts  = async () => { const ids = await fanOut(); if (ids.length) nav('/app/drafts'); };
  const publishNow  = async () => {
    const when = new Date(Date.now() + 90_000);  // +90s minimum for the schedule API
    const ids = await fanOut(when);
    if (ids.length) nav('/app/drafts');
  };
  const scheduleAtCustom = async () => {
    const when = new Date(scheduleAt);
    if (when.getTime() < Date.now() + 60_000) return toast('Pick a time at least a minute from now', 'warning');
    const ids = await fanOut(when);
    if (ids.length) { setShowSchedule(false); nav('/app/drafts'); }
  };

  const saveAsTemplate = async () => {
    if (!project?.id) return toast('No project', 'error');
    if (!tplName.trim()) return toast('Template needs a name', 'warning');
    if (!currentCaption.trim()) return toast('Caption is empty', 'warning');
    const r = await createTemplate({
      input: { projectId: project.id, name: tplName, category: tplCategory, platform: active, body: currentCaption },
    });
    if (r.error) return toast(r.error.message, 'error');
    toast(`Saved as template: ${tplName}`);
    setShowTemplate(false);
    setTplName('');
  };

  // Count image/video/gif from a media-id list by mime_type:
  //   image/* (excl image/gif) → image; video/* → video; image/gif → gif.
  // Untyped assets (no mime handy) default to images — used by thread segments.
  const xSegmentCounts = (ids: string[]) => {
    let imageCount = 0, videoCount = 0, gifCount = 0;
    for (const id of ids) {
      const mime: string | undefined = mediaById[id]?.mimeType;
      if (mime === 'image/gif') gifCount++;
      else if (mime?.startsWith('video/')) videoCount++;
      else imageCount++; // image/* or untyped → image
    }
    return { imageCount, videoCount, gifCount };
  };

  // Derive X media counts for a platform's top-level attachments. Prefer the
  // media asset's mime_type; fall back to inferring from the chosen post type
  // when the asset model has no mime handy.
  const xMediaCounts = (p: string) => {
    const ids = mediaByPlatform[p] ?? [];
    let imageCount = 0, videoCount = 0, gifCount = 0;
    let typed = false;
    for (const id of ids) {
      const mime: string | undefined = mediaById[id]?.mimeType;
      if (!mime) continue;
      typed = true;
      if (mime === 'image/gif') gifCount++;
      else if (mime.startsWith('image/')) imageCount++;
      else if (mime.startsWith('video/')) videoCount++;
    }
    if (!typed && ids.length > 0) {
      const pt = postType[p] ?? defaultPostType[p as Platform];
      if (pt === 'video') videoCount = ids.length;
      else if (pt === 'gif') gifCount = ids.length;
      else imageCount = ids.length; // single-image / multi-image
    }
    return { imageCount, videoCount, gifCount };
  };

  // Client-side rule-based validator
  const validation = useMemo(() => {
    const out: { platform: string; checks: { severity: 'success'|'warning'|'blocking'|'suggestion'; category: string; message: string }[] }[] = [];
    for (const p of selected) {
      const cap = captionFor(p);
      const limit = effLimit(p);
      const checks: any[] = [];
      if (cap.length > limit) checks.push({ severity: 'blocking', category: 'Character limit', message: `Over ${limit} (currently ${cap.length}).` });
      else if (cap.length === 0) checks.push({ severity: 'blocking', category: 'Empty', message: 'Caption is empty.' });
      else checks.push({ severity: 'success', category: 'Character limit', message: `${cap.length}/${limit} — within limit.` });
      const tags = (cap.match(/#\w+/g) ?? []).length;
      if (p === 'linkedin' && cap.length > 0) {
        // Content-type + hashtag rules from the shared validator (same rules the
        // publish worker re-checks). Caption length already covered generically above.
        for (const c of validateLinkedInPost({
          contentType: (postType[p] ?? defaultPostType.linkedin) as any,
          caption: cap,
          assetCount: mediaByPlatform[p]?.length ?? 0,
          linkUrl: linkUrlByPlatform[p] ?? null,
        })) {
          if (c.code === 'caption_length') continue;
          checks.push({ severity: c.severity, category: 'LinkedIn', message: c.message });
        }
      }
      if (p === 'twitter') {
        // Content-type / media / poll / thread rules from the shared X validator
        // (same rules the publish worker re-checks). longPosts + tierConfigured
        // are now capability-derived from the selected connection.
        const pt = (postType[p] ?? defaultPostType.twitter) as XValidationInput['contentType'];
        const isThreadType = pt === 'thread';
        // Skip the empty-caption pass for non-thread types with no caption — the
        // generic Empty check above already covers it. Threads have no top-level
        // caption (segments carry the text), so always validate threads.
        if (cap.length > 0 || isThreadType) {
          const counts = xMediaCounts(p);
          const segs = (threadByPlatform[p] ?? [{ text: '', mediaIds: [] }]);
          const xInput: XValidationInput = {
            contentType: pt,
            caption: isThreadType ? '' : cap,
            imageCount: isThreadType ? 0 : counts.imageCount,
            videoCount: isThreadType ? 0 : counts.videoCount,
            gifCount: isThreadType ? 0 : counts.gifCount,
            poll: pt === 'poll' ? (pollByPlatform[p] ?? { options: ['', ''], durationMinutes: 1440 }) : null,
            segments: isThreadType
              ? segs.map((s) => {
                  const c = xSegmentCounts(s.mediaIds);
                  return { text: s.text, imageCount: c.imageCount, videoCount: c.videoCount, gifCount: c.gifCount };
                })
              : null,
            longPosts: xLongPostsFor(p),
            tierConfigured: xTierConfiguredFor(p),
          };
          for (const c of validateXPost(xInput)) {
            // Drop the top-level caption_length check only for non-thread types,
            // where the generic counter already covers it. Thread per-segment
            // length checks have different codes and should pass through.
            if (c.code === 'caption_length' && !isThreadType) continue;
            checks.push({ severity: c.severity === 'info' ? 'suggestion' : c.severity, category: 'X', message: c.message });
          }
        }
      }
      if (p === 'instagram' && tags < 5 && cap.length > 0) checks.push({ severity: 'warning', category: 'Hashtags', message: `Only ${tags}. Instagram favours 8–15.` });
      if (p === 'twitter'   && tags > 2)                   checks.push({ severity: 'warning', category: 'Hashtags', message: `${tags} on X may look spammy — 1–2 perform best.` });
      if (cap.length > 0 && !/[?!]/.test(cap)) checks.push({ severity: 'suggestion', category: 'Engagement', message: 'Consider a question or CTA.' });
      const hasEmoji = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(cap);
      if (!hasEmoji && cap.length > 80) checks.push({ severity: 'suggestion', category: 'Tone', message: 'One emoji can lift CTR ~12%.' });
      out.push({ platform: p, checks });
    }
    return out;
  }, [selected, sharedCaption, overrides, postType, mediaByPlatform, linkUrlByPlatform, pollByPlatform, xRefByPlatform, threadByPlatform, accountByPlatform, connections]);
  const blockingCount = validation.flatMap((v) => v.checks).filter((c) => c.severity === 'blocking').length;
  const warningCount  = validation.flatMap((v) => v.checks).filter((c) => c.severity === 'warning').length;

  const activeLimit = effLimit(active);
  const activeLen = currentCaption.length;
  const activeOver = activeLen > activeLimit;
  const activeConn = connFor(active);
  // X tier gate: twitter selected but the connection has no paid API tier.
  const xTierMissing = selected.includes('twitter') && !xTierConfiguredFor('twitter');

  const platformStats = useMemo(() => {
    const m: Record<string, { len: number; over: boolean }> = {};
    for (const p of selected) {
      const len = captionFor(p).length;
      m[p] = { len, over: len > effLimit(p) };
    }
    return m;
  }, [selected, sharedCaption, overrides, accountByPlatform, connections]);

  // Selected platforms whose caption is over their effective cap — drives the
  // inline "Customize for X" nudge below the caption box. Threads carry their
  // text per-segment (top-level caption empty) so they never appear here.
  const overLimitPlatforms = useMemo(() =>
    selected
      .filter((p) => !(p === 'twitter' && (postType[p] ?? defaultPostType.twitter) === 'thread'))
      .map((p) => ({ p, over: (platformStats[p]?.len ?? 0) - effLimit(p) }))
      .filter((x) => x.over > 0),
  [selected, platformStats, postType, accountByPlatform, connections]);

  const authorDisplay = user?.displayName || user?.email?.split('@')[0] || 'Sarah Chen';
  const wsDisplay = workspace_name || 'Acme Corp';

  if (projects.fetching) return <div className="p-8 text-stone-500">Loading…</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto min-w-0">
      <div className="grid grid-cols-1 xl:grid-cols-[7fr_3fr] gap-8 min-w-0">
        {/* ── LEFT: COMPOSER ─────────────────────────────────────────── */}
        <div className="space-y-6 min-w-0">
          <h1 className="text-2xl font-semibold text-stone-900">Create posts</h1>

          {prefillTitle ? (
            <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg border border-violet-200 bg-violet-50 text-sm text-violet-800">
              <span className="min-w-0 truncate">Composing from: <strong className="font-medium">{prefillTitle}</strong></span>
              <div className="flex-shrink-0 flex items-center gap-3">
                <button type="button" onClick={() => { setSourceSearch(''); setShowSourcePicker(true); }}
                  className="text-violet-600 hover:text-violet-800 font-medium">Change</button>
                <button type="button" onClick={detachSource}
                  className="text-violet-500 hover:text-violet-700" title="Remove source link">✕</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => { setSourceSearch(''); setShowSourcePicker(true); }}
              className="inline-flex items-center gap-1.5 text-[13px] text-stone-500 hover:text-violet-700">
              📎 Compose from a library item
            </button>
          )}

          {/* PUBLISHING TO — only the platforms the user has a live connection for. */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Publishing to</p>
              <NavLink to="/app/connections" className="text-[11px] text-violet-600 hover:underline">Manage connections →</NavLink>
            </div>
            {connectedPlatforms.length === 0 ? (
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">
                No accounts connected yet.{' '}
                <NavLink to="/app/connections" className="font-medium underline">Connect one</NavLink>{' '}
                to start posting.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {connectedPlatforms.map((p) => {
                  const on = selected.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => togglePlatform(p.id)} type="button"
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium ${on ? 'bg-violet-50 border border-violet-300 text-violet-800 ring-1 ring-violet-200' : 'bg-white border border-stone-200 text-stone-700 hover:bg-stone-50'}`}>
                      <span className={`w-6 h-6 rounded ${p.bg} text-white flex items-center justify-center text-xs font-bold`}>{p.glyph}</span>
                      <span>{p.label}</span>
                      {on && <span className="text-violet-600 text-base leading-none">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tabs */}
          {selected.length > 0 && (
            <div className="border-b border-stone-200">
              <div className="flex gap-1 -mb-px overflow-x-auto">
                {selected.map((p) => {
                  const meta = PLATFORMS.find((x) => x.id === p)!;
                  const stats = platformStats[p];
                  const isActive = active === p;
                  return (
                    <button key={p} onClick={() => setActive(p)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${isActive ? 'border-violet-600 text-violet-700' : 'border-transparent text-stone-500 hover:text-stone-800'}`}>
                      <span className={`w-5 h-5 rounded ${meta.bg} text-white flex items-center justify-center text-[10px] font-bold`}>{meta.glyph}</span>
                      <span>{meta.label}</span>
                      {isOverridden(p) && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">custom</span>}
                      <span className={`text-[10px] ${stats.over ? 'text-red-600 font-medium' : 'text-stone-400'}`}>{stats.len}/{effLimit(p)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* POSTING AS — pick which account/Page when a platform has more than one
              (e.g. LinkedIn personal profile + Company Pages). */}
          {selected.length > 0 && connsFor(active).length > 1 && (
            <div className="flex items-center gap-2">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Posting as</p>
              <select
                value={connFor(active)?.id ?? ''}
                onChange={(e) => setAccountByPlatform((m) => ({ ...m, [active]: e.target.value }))}
                className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                {connsFor(active).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.accountLabel}{c.accountType === 'page' ? '' : ' · Profile'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* POST TYPE — per-active-platform catalog */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Post type</p>
              <p className="text-[10px] text-stone-400">Each platform keeps its own selection</p>
            </div>
            <div className={`grid gap-3 ${activeCards.length >= 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
              {activeCards.map((t) => {
                const on = currentPostType === t.id;
                return (
                  <button key={t.id} onClick={() => setCurrentPostType(t.id)} type="button"
                    className={`p-4 rounded-xl text-center ${on ? 'bg-violet-100 border-2 border-violet-400 ring-1 ring-violet-200' : 'bg-white border-2 border-stone-200 hover:border-stone-300'}`}>
                    <div className="text-2xl mb-1">{t.icon}</div>
                    <div className="font-medium text-sm text-stone-900">{t.title}</div>
                    <div className="text-[11px] text-stone-500 mt-0.5">{t.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* AI generate */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-violet-800">
              ✨ Generate with AI {selected.length > 1 && <span className="text-xs font-normal text-violet-600">(active tab: {active})</span>}
            </div>
            <div className="flex gap-2">
              <input value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="Describe the post idea…"
                className="flex-1 px-3 py-2 border border-violet-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="px-3 py-2 border border-violet-200 rounded-lg text-sm bg-white">
                {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={generate} disabled={generating || noCredits}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white rounded-lg text-sm font-medium whitespace-nowrap">
                {generating ? 'Generating…' : 'Generate'}
              </button>
            </div>
            {noCredits && (
              <p className="text-[11px] text-amber-700">
                Out of credits — <NavLink to="/app/billing" className="font-medium underline">top up</NavLink> to use AI.
              </p>
            )}
          </div>

          {/* X tier gate — twitter selected but connection has no paid API tier.
              validateXPost already emits a blocking tier_required check (gating
              Publish/Schedule); this surfaces it inline near the composer. */}
          {active === 'twitter' && selected.includes('twitter') && xTierMissing && (
            <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800">
              <span className="min-w-0">⛔ Needs a paid X API tier to publish. {activeConn ? '' : 'Connect an X account first. '}Posts can be saved as drafts but won't publish until the connected account has an active tier.</span>
              <NavLink to="/app/connections" className="flex-shrink-0 text-red-700 font-medium underline whitespace-nowrap">Set up →</NavLink>
            </div>
          )}

          {/* Caption — hidden for the X thread builder, which renders below. */}
          {selected.length > 0 && !(active === 'twitter' && currentPostType === 'thread') && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-stone-600">
                  Caption {selected.length > 1 && (<span className="ml-2 text-stone-400 font-normal">{isOverridden(active) ? `Custom for ${active}` : 'Shared across platforms'}</span>)}
                </div>
                <div className="flex items-center gap-3">
                  {selected.length > 1 && (
                    <button onClick={enableOverride} className="text-[11px] text-violet-700 hover:underline">
                      {isOverridden(active) ? '↶ Reset to shared' : `✏ Customize for ${active}`}
                    </button>
                  )}
                  <span className={`text-[11px] ${activeOver ? 'text-red-600 font-medium' : 'text-stone-400'}`}>{activeLen} / {activeLimit}</span>
                </div>
              </div>
              <textarea ref={textareaRef} value={currentCaption} onChange={(e) => setCurrentCaption(e.target.value)} rows={9}
                placeholder="Write or generate your post…"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none ${activeOver ? 'border-red-300' : 'border-stone-300'}`} />

              {/* Over-limit nudge — trim a shorter platform-specific caption. */}
              {overLimitPlatforms.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-red-700">
                  <span aria-hidden>⚠️</span>
                  {overLimitPlatforms.map(({ p, over }, i) => {
                    const label = PLATFORMS.find((x) => x.id === p)?.label ?? p;
                    return (
                      <span key={p} className="inline-flex items-center gap-1">
                        {i > 0 && <span className="text-red-300">·</span>}
                        <span>{label} is over by {over}</span>
                        {!isOverridden(p) && (
                          <button type="button" onClick={() => customizeFor(p)} className="text-violet-700 hover:underline font-medium">
                            Customize for {label}
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                <div className="relative">
                  <button onClick={() => { setShowHashtags(s => !s); setShowEmoji(false); setShowMentions(false); }} className="px-2.5 py-1.5 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50 font-medium"># Hashtags</button>
                  {showHashtags && (() => {
                    const derived = deriveHashtags(currentCaption);
                    return (
                      <div className="absolute z-20 mt-1 p-2 bg-white border border-stone-200 rounded-lg shadow-lg w-72">
                        {/* (b) caption-derived suggestions — pure local heuristic */}
                        {derived.length > 0 && (
                          <>
                            <p className="text-[10px] text-stone-400 mb-1 px-1">From your text</p>
                            <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b border-stone-100">
                              {derived.map((h) => (
                                <button key={h} onClick={() => insertAtCaret(' ' + h)} className="px-2 py-1 text-[11px] bg-violet-50 hover:bg-violet-100 text-violet-700 rounded">{h}</button>
                              ))}
                            </div>
                          </>
                        )}
                        {/* (a) add-your-own free-text input */}
                        <div className="flex gap-1 mb-2">
                          <input value={hashtagInput} onChange={(e) => setHashtagInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomHashtag(); } }}
                            placeholder="add your own" autoFocus
                            className="flex-1 min-w-0 px-2 py-1 border border-stone-200 rounded text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400" />
                          <button onClick={addCustomHashtag} className="px-2 py-1 text-[11px] bg-violet-600 hover:bg-violet-700 text-white rounded font-medium">Add</button>
                        </div>
                        <p className="text-[10px] text-stone-400 mb-1 px-1">Popular</p>
                        <div className="flex flex-wrap gap-1">
                          {COMMON_HASHTAGS.map((h) => (
                            <button key={h} onClick={() => insertAtCaret(' ' + h)} className="px-2 py-1 text-[11px] bg-stone-50 hover:bg-violet-50 text-stone-700 rounded">{h}</button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="relative">
                  <button onClick={() => { setShowMentions(s => !s); setShowEmoji(false); setShowHashtags(false); }} className="px-2.5 py-1.5 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50 font-medium">@ Mention</button>
                  {showMentions && (
                    <div className="absolute z-20 mt-1 p-2.5 bg-white border border-stone-200 rounded-lg shadow-lg w-72">
                      <p className="text-[10px] text-stone-400 mb-1 px-0.5">Mention an account</p>
                      <div className="flex gap-1">
                        <span className="flex items-center text-stone-400 text-sm">@</span>
                        <input value={mentionInput} onChange={(e) => setMentionInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMention(); } }}
                          placeholder="handle (without @)" autoFocus
                          className="flex-1 min-w-0 px-2 py-1 border border-stone-200 rounded text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400" />
                        <button onClick={addMention} className="px-2 py-1 text-[11px] bg-violet-600 hover:bg-violet-700 text-white rounded font-medium">Add</button>
                      </div>
                      {active === 'linkedin' && (
                        <p className="text-[10px] text-stone-400 mt-1.5 px-0.5">LinkedIn renders true @mentions only for resolved profiles — plain @text posts as text.</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button onClick={() => { setShowEmoji(s => !s); setShowHashtags(false); setShowMentions(false); }} className="px-2.5 py-1.5 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50 font-medium">😊 Emoji</button>
                  {showEmoji && (
                    <div className="absolute z-20 mt-1 p-2 bg-white border border-stone-200 rounded-lg shadow-lg w-64">
                      <div className="grid grid-cols-10 gap-1">
                        {COMMON_EMOJIS.map((e) => (
                          <button key={e} onClick={() => { insertAtCaret(e); setShowEmoji(false); }} className="text-lg hover:bg-stone-100 rounded">{e}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={aiRewrite} disabled={rewriting || noCredits} title={noCredits ? 'Out of credits — top up to use AI' : undefined} className="px-2.5 py-1.5 rounded-md text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 disabled:opacity-60 font-medium">
                  ✨ AI rewrite {rewriting ? '…' : '(2⚡)'}
                </button>
              </div>

              {/* Captured @mentions — removable chips (per platform). */}
              {currentMentions.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Mentions</span>
                  {currentMentions.map((h) => (
                    <span key={h} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-[11px] text-violet-700">
                      @{h}
                      <button onClick={() => removeMention(h)} title="Remove mention" className="text-violet-400 hover:text-violet-700 leading-none">×</button>
                    </span>
                  ))}
                </div>
              )}

              {!activeConn && <p className="text-[11px] text-amber-600 mt-2">No connected {active} account — draft saves but can't publish until connected.</p>}
            </div>
          )}

          {/* X THREAD BUILDER — twitter + thread. Ordered segment editor with
              per-segment text + media; replaces the single caption box above. */}
          {active === 'twitter' && selected.includes('twitter') && currentPostType === 'thread' && (
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Thread</p>
                <p className="text-[10px] text-stone-400">{currentThread.length} segment{currentThread.length > 1 ? 's' : ''}</p>
              </div>
              <div className="space-y-3">
                {currentThread.map((seg, i) => {
                  const over = seg.text.length > activeLimit;
                  return (
                    <div key={i} className="border border-stone-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-stone-600">{i + 1}/{currentThread.length}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] ${over ? 'text-red-600 font-medium' : 'text-stone-400'}`}>{seg.text.length} / {activeLimit}</span>
                          <button type="button" onClick={() => moveSegment(i, -1)} disabled={i === 0}
                            title="Move up" className="w-6 h-6 rounded border border-stone-200 text-stone-500 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed">↑</button>
                          <button type="button" onClick={() => moveSegment(i, 1)} disabled={i === currentThread.length - 1}
                            title="Move down" className="w-6 h-6 rounded border border-stone-200 text-stone-500 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed">↓</button>
                          <button type="button" onClick={() => removeSegment(i)} disabled={currentThread.length <= 1}
                            title="Remove segment" className="w-6 h-6 rounded border border-stone-200 text-stone-500 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed">×</button>
                        </div>
                      </div>
                      <textarea value={seg.text} onChange={(e) => setSegmentText(i, e.target.value)} rows={3}
                        placeholder={i === 0 ? 'Start your thread…' : `Tweet ${i + 1}…`}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none ${over ? 'border-red-300' : 'border-stone-300'}`} />
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <button type="button" onClick={() => setThreadMediaPickerFor(i)}
                          className="px-2.5 py-1.5 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50 text-xs font-medium">
                          ◉ Media{seg.mediaIds.length ? ` (${seg.mediaIds.length})` : ''}
                        </button>
                        {seg.mediaIds.map((id) => {
                          const m = mediaById[id];
                          return (
                            <div key={id} className="relative w-12 h-12 rounded-md overflow-hidden border border-stone-200 bg-stone-100 group">
                              {m?.storageKey
                                ? <img src={m.storageKey} alt={m.altText ?? ''} className="w-full h-full object-cover"
                                    onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
                                : <div className="w-full h-full flex items-center justify-center text-[9px] text-stone-400">missing</div>}
                              <button type="button" onClick={() => toggleSegmentMedia(i, id)} title="Remove"
                                className="absolute top-0 right-0 w-4 h-4 rounded-bl bg-stone-900/80 text-white text-[10px] leading-none opacity-0 group-hover:opacity-100">×</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" onClick={addSegment}
                className="mt-2 text-[11px] text-violet-700 hover:underline">+ Add segment</button>
            </div>
          )}

          {/* MEDIA — hidden for text/poll/thread (no top-level media on those) */}
          {selected.length > 0 && currentPostType !== 'text' && currentPostType !== 'poll' && !(active === 'twitter' && currentPostType === 'thread') && (
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Media for {active}</p>
                <p className="text-[10px] text-stone-400">{currentMediaIds.length} attached</p>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <button onClick={() => setShowLibrary(true)} type="button"
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white hover:bg-stone-50 font-medium">◉ Browse library</button>
                <button onClick={() => setShowAddUrl(true)} type="button"
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white hover:bg-stone-50 font-medium">↑ Add via URL</button>
                <button onClick={aiGenerateImage} disabled={generatingImage || noCredits} type="button"
                  title={noCredits ? 'Out of credits — top up to use AI' : undefined}
                  className="px-3 py-2 border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg text-sm font-medium disabled:opacity-60">
                  ✨ AI generate {generatingImage ? '…' : '(10⚡)'}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {currentMediaIds.map((id) => {
                  const m = mediaById[id];
                  const e = editFor(id);
                  return (
                    <div key={id} className="relative group aspect-square bg-stone-100 rounded-lg overflow-hidden border border-stone-200">
                      {m?.storageKey
                        ? <img src={m.storageKey} alt={m.altText ?? ''} style={editStyle(id)} className="w-full h-full object-cover transition-transform"
                            onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.opacity='0.3'; }} />
                        : <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs">missing asset</div>}
                      {/* Top-right: remove */}
                      <button onClick={() => removeMedia(id)} title="Remove"
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-stone-900/80 text-white text-xs opacity-0 group-hover:opacity-100 hover:bg-red-600">×</button>
                      {/* Bottom: image tools */}
                      <div className="absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                        <button onClick={() => rotate90(id)} title="Rotate 90°"
                          className="px-2 py-1 text-[10px] bg-stone-900/80 text-white rounded">⟲ {e.rotate}°</button>
                        <button onClick={() => cycleFilter(id)} title="Cycle filter preset"
                          className="px-2 py-1 text-[10px] bg-stone-900/80 text-white rounded">⊕ {e.filter}</button>
                      </div>
                    </div>
                  );
                })}
                <button onClick={() => setShowLibrary(true)} type="button"
                  className="aspect-square border-2 border-dashed border-stone-300 rounded-lg text-stone-400 hover:border-violet-400 hover:text-violet-600 text-3xl">+</button>
              </div>
              <div className="mt-2 text-[10px] text-stone-400">
                Image tools applied locally to the preview (rotate ⟲, filter ⊕). Crop &amp; text overlay coming soon.
              </div>
            </div>
          )}

          {/* Link URL — required for the 'link' post type (LinkedIn link share). */}
          {selected.length > 0 && currentPostType === 'link' && (
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Link URL</p>
                <p className="text-[10px] text-stone-400">Required — LinkedIn fetches the preview card</p>
              </div>
              <input
                type="url"
                value={currentLinkUrl}
                onChange={(e) => setCurrentLinkUrl(e.target.value)}
                placeholder="https://example.com/article"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          )}

          {/* X poll editor — twitter + poll post type. 2–4 options + duration. */}
          {active === 'twitter' && selected.includes('twitter') && currentPostType === 'poll' && (
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Poll</p>
                <p className="text-[10px] text-stone-400">{currentPoll.options.length}/{X_POLL_MAX_OPTIONS} options</p>
              </div>
              <div className="space-y-2">
                {currentPoll.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={opt}
                      maxLength={X_POLL_OPTION_MAX}
                      onChange={(e) => setPollOption(i, e.target.value)}
                      placeholder={`Choice ${i + 1}`}
                      className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                    <span className={`text-[11px] w-12 text-right ${opt.length >= X_POLL_OPTION_MAX ? 'text-red-600 font-medium' : 'text-stone-400'}`}>{opt.length}/{X_POLL_OPTION_MAX}</span>
                    <button
                      type="button"
                      onClick={() => removePollOption(i)}
                      disabled={currentPoll.options.length <= X_POLL_MIN_OPTIONS}
                      title="Remove option"
                      className="w-7 h-7 rounded-md border border-stone-200 text-stone-500 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed">×</button>
                  </div>
                ))}
              </div>
              {currentPoll.options.length < X_POLL_MAX_OPTIONS && (
                <button type="button" onClick={addPollOption}
                  className="mt-2 text-[11px] text-violet-700 hover:underline">+ Add option</button>
              )}
              <div className="mt-3">
                <span className="text-xs font-medium text-stone-600">Poll length</span>
                <select
                  value={currentPoll.durationMinutes}
                  onChange={(e) => setCurrentPoll({ durationMinutes: Number(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                  {POLL_DURATIONS.map((d) => <option key={d.minutes} value={d.minutes}>{d.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* X quote / reply — twitter, any post type. Optional. */}
          {active === 'twitter' && selected.includes('twitter') && (
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Quote or reply</p>
                <p className="text-[10px] text-stone-400">Optional</p>
              </div>
              <div className="flex items-center gap-4 mb-2 text-xs text-stone-600">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="xRefMode" checked={(currentXRef.mode ?? 'quote') === 'quote'} onChange={() => setXRefMode('quote')} />
                  Quote
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="xRefMode" checked={currentXRef.mode === 'reply'} onChange={() => setXRefMode('reply')} />
                  Reply
                </label>
              </div>
              <input
                value={currentXRefRaw}
                onChange={(e) => setXRefRaw(e.target.value)}
                placeholder="Paste X URL or post ID (e.g. https://x.com/user/status/1234567890)"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              {currentXRefRaw.trim() && (
                currentXRef.postId
                  ? <p className="text-[11px] text-emerald-600 mt-1">{currentXRef.mode === 'reply' ? 'Replying to' : 'Quoting'} post {currentXRef.postId}</p>
                  : <p className="text-[11px] text-amber-600 mt-1">Couldn't find a post ID — paste a full status URL or a numeric ID.</p>
              )}
            </div>
          )}

          {/* LinkedIn-only: Visibility + Comments */}
          {active === 'linkedin' && selected.includes('linkedin') && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-stone-600">Visibility</span>
                <select value={currentMeta.visibility ?? 'PUBLIC'} onChange={(e) => setCurrentMeta({ visibility: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                  <option value="PUBLIC">Anyone</option>
                  <option value="CONNECTIONS">Connections only</option>
                  <option value="LOGGED_IN">Logged-in members</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-stone-600">Comments</span>
                <select value={currentMeta.comments ?? 'ALL'} onChange={(e) => setCurrentMeta({ comments: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                  <option value="ALL">Anyone</option>
                  <option value="CONNECTIONS_ONLY">Connections only</option>
                  <option value="NONE">No one</option>
                </select>
              </label>
            </div>
          )}

          {/* AI Validator panel */}
          {showValidator && selected.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-stone-900">✨ AI Validator</div>
                <button onClick={() => setShowValidator(false)} className="text-xs text-stone-400 hover:text-stone-600">Hide</button>
              </div>
              {validation.map((v) => (
                <div key={v.platform} className="border-t border-stone-100 pt-3 first:border-0 first:pt-0">
                  <p className="text-xs font-medium text-stone-600 capitalize mb-1.5">{v.platform}</p>
                  <ul className="space-y-1">
                    {v.checks.map((c, i) => {
                      const icon = c.severity === 'blocking' ? '⛔' : c.severity === 'warning' ? '⚠️' : c.severity === 'success' ? '✅' : '💡';
                      const color = c.severity === 'blocking' ? 'text-red-700' : c.severity === 'warning' ? 'text-amber-700' : c.severity === 'success' ? 'text-emerald-700' : 'text-stone-600';
                      return <li key={i} className={`text-[12px] ${color} flex items-start gap-2`}><span>{icon}</span><span><strong>{c.category}:</strong> {c.message}</span></li>;
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-stone-100">
            <div className="flex items-center gap-2 text-[11px] text-stone-500">
              <button onClick={() => setShowValidator((s) => !s)}
                className={`px-3 py-1.5 rounded-md font-medium border ${blockingCount > 0 ? 'border-red-200 text-red-700 bg-red-50' : warningCount > 0 ? 'border-amber-200 text-amber-700 bg-amber-50' : 'border-violet-200 text-violet-700 bg-violet-50'}`}>
                ✨ AI Validator {blockingCount > 0 ? `· ${blockingCount} blocking` : warningCount > 0 ? `· ${warningCount} warning${warningCount > 1 ? 's' : ''}` : '· OK'}
              </button>
              <button onClick={() => setShowTemplate(true)} className="px-3 py-1.5 rounded-md text-stone-600 border border-stone-200 hover:bg-stone-50 font-medium">📄 Save as template</button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => nav('/app/drafts')} className="text-stone-600 px-3 py-2 text-sm hover:underline">Cancel</button>
              <button onClick={saveDrafts} disabled={saving || selected.length === 0 || blockingCount > 0} className="px-4 py-2 border border-stone-300 text-stone-700 rounded-lg text-sm hover:bg-stone-50 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              <div className="relative">
                <button onClick={() => setShowSchedule((s) => !s)} disabled={saving || selected.length === 0 || blockingCount > 0}
                  className="px-4 py-2 border border-violet-300 text-violet-700 rounded-lg text-sm hover:bg-violet-50 disabled:opacity-50">Schedule</button>
                {showSchedule && (
                  <div className="absolute right-0 bottom-full mb-2 z-30 bg-white border border-stone-200 rounded-xl shadow-lg p-4 w-72">
                    <p className="text-xs font-medium text-stone-700 mb-2">Schedule {selected.length} post{selected.length > 1 ? 's' : ''}</p>
                    <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                    <p className="text-[10px] text-stone-400 mt-1">Minimum 1 minute from now.</p>
                    <div className="mt-3 flex justify-end gap-2">
                      <button onClick={() => setShowSchedule(false)} className="text-xs text-stone-500 hover:underline">Cancel</button>
                      <button onClick={scheduleAtCustom} disabled={saving} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-xs font-medium disabled:opacity-50">{saving ? 'Scheduling…' : 'Schedule'}</button>
                    </div>
                  </div>
                )}
              </div>
              <button onClick={publishNow} disabled={saving || selected.length === 0 || blockingCount > 0}
                className="px-5 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white rounded-lg text-sm font-medium">
                {saving ? 'Publishing…' : 'Publish now'}
              </button>
            </div>
          </div>
        </div>

        {/* ── RIGHT: STICKY PREVIEW PANE — one card per SELECTED target ── */}
        {selected.length > 0 && (
          <aside className="xl:sticky xl:top-6 xl:self-start space-y-3">
            <div className="flex items-center justify-between gap-3 px-1">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                {selected.length > 1 ? `${selected.length} previews` : 'Preview'}
              </p>
              <select value={previewViewport} onChange={(e) => setPreviewViewport(e.target.value as any)}
                className="text-xs px-2.5 py-1.5 border border-stone-200 rounded-md bg-white shadow-sm">
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile</option>
              </select>
            </div>
            {selected.map((p) => {
              const meta = PLATFORMS.find((x) => x.id === p)!;
              const conn = connFor(p);
              const pType = postType[p] ?? defaultPostType[p as Platform] ?? 'single-image';
              const pMediaIds = mediaByPlatform[p] ?? [];
              const pMediaUrls = pMediaIds.map((id) => mediaById[id]?.storageKey).filter(Boolean) as string[];
              const pPoll = pollByPlatform[p] ?? { options: ['', ''], durationMinutes: 1440 };
              const pXRef = xRefByPlatform[p] ?? { mode: null, postId: null };
              const pThread = threadByPlatform[p] ?? [{ text: '', mediaIds: [] }];
              const isActive = active === p;
              return (
                <div key={p}
                  className={`bg-stone-100 rounded-2xl p-4 space-y-3 ${isActive && selected.length > 1 ? 'ring-2 ring-violet-300' : ''}`}>
                  <button type="button" onClick={() => setActive(p)} className="flex items-start gap-3 min-w-0 w-full text-left">
                    <span className={`w-10 h-10 rounded-lg ${meta.bg} text-white flex items-center justify-center text-base font-bold flex-shrink-0`}>{meta.glyph}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-stone-900 truncate">
                        {meta.label} preview {isActive && selected.length > 1 && <span className="text-[10px] font-normal text-violet-600">· editing</span>}
                      </p>
                      <p className="text-[11px] text-stone-500 capitalize">{pType.replace('-', ' ')}</p>
                    </div>
                  </button>
                  <PlatformPreview
                    platform={p}
                    caption={captionFor(p)}
                    postType={pType}
                    /* Identity comes from the SELECTED connection; workspace/user
                       values are only the last-resort fallback when unconnected. */
                    authorName={conn?.accountLabel || authorDisplay}
                    workspaceName={conn?.accountLabel || wsDisplay}
                    avatarUrl={conn?.avatarUrl}
                    followerCount={conn?.followerCount}
                    viewport={previewViewport}
                    mediaUrls={pMediaUrls}
                    heroEdit={pMediaIds[0] ? editFor(pMediaIds[0]) : undefined}
                    poll={p === 'twitter' && pType === 'poll' ? pPoll : undefined}
                    xRef={p === 'twitter' && pXRef.mode === 'quote' && pXRef.postId ? pXRef : undefined}
                    thread={p === 'twitter' && pType === 'thread'
                      ? pThread.map((s) => ({ text: s.text, mediaUrls: s.mediaIds.map((id) => mediaById[id]?.storageKey).filter(Boolean) as string[] }))
                      : undefined}
                  />
                </div>
              );
            })}
          </aside>
        )}
      </div>

      {/* Browse media library modal */}
      {showLibrary && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-6 bg-stone-900/40" onClick={() => setShowLibrary(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl p-6 w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-stone-900">Browse media library</h2>
              <button onClick={() => setShowLibrary(false)} className="text-stone-400 hover:text-stone-700">✕</button>
            </div>
            <p className="text-xs text-stone-500 mb-3">Click an image to attach to <strong>{active}</strong>. {currentMediaIds.length} already attached.</p>
            <div className="flex-1 overflow-y-auto -mx-2 px-2">
              {mediaQ.fetching ? (
                <p className="text-stone-500 text-sm">Loading…</p>
              ) : mediaLibrary.length === 0 ? (
                <div className="text-center py-12 text-stone-400 text-sm">No media yet. Add one via URL or AI generate.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {mediaLibrary.map((m: any) => {
                    const on = currentMediaIds.includes(m.id);
                    return (
                      <button key={m.id} onClick={() => { on ? removeMedia(m.id) : attachMedia(m.id); }}
                        className={`relative aspect-square overflow-hidden rounded-lg border-2 ${on ? 'border-violet-500 ring-2 ring-violet-200' : 'border-stone-200 hover:border-stone-300'}`}>
                        <img src={m.storageKey} alt={m.altText ?? ''} className="w-full h-full object-cover"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
                        {on && <span className="absolute top-1 right-1 w-6 h-6 rounded-full bg-violet-600 text-white text-xs flex items-center justify-center">✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-end gap-3">
              <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*,application/pdf" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="text-xs text-violet-700 hover:underline disabled:opacity-50">{uploading ? 'Uploading…' : '⬆ Upload file'}</button>
              <button onClick={() => { setShowLibrary(false); setShowAddUrl(true); }} className="text-xs text-violet-700 hover:underline">+ Add via URL</button>
              <button onClick={() => setShowLibrary(false)} className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-sm font-medium">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Thread segment media picker — reuses the library, scoped to one segment. */}
      {threadMediaPickerFor !== null && (() => {
        const segIdx = threadMediaPickerFor;
        const seg = currentThread[segIdx];
        const segMediaIds = seg?.mediaIds ?? [];
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center p-6 bg-stone-900/40" onClick={() => setThreadMediaPickerFor(null)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl p-6 w-full max-w-3xl max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-stone-900">Media for thread segment {segIdx + 1}</h2>
                <button onClick={() => setThreadMediaPickerFor(null)} className="text-stone-400 hover:text-stone-700">✕</button>
              </div>
              <p className="text-xs text-stone-500 mb-3">Click to attach/detach. {segMediaIds.length} attached to this segment.</p>
              <div className="flex-1 overflow-y-auto -mx-2 px-2">
                {mediaQ.fetching ? (
                  <p className="text-stone-500 text-sm">Loading…</p>
                ) : mediaLibrary.length === 0 ? (
                  <div className="text-center py-12 text-stone-400 text-sm">No media yet. Add one via URL or AI generate.</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {mediaLibrary.map((m: any) => {
                      const on = segMediaIds.includes(m.id);
                      return (
                        <button key={m.id} onClick={() => toggleSegmentMedia(segIdx, m.id)}
                          className={`relative aspect-square overflow-hidden rounded-lg border-2 ${on ? 'border-violet-500 ring-2 ring-violet-200' : 'border-stone-200 hover:border-stone-300'}`}>
                          <img src={m.storageKey} alt={m.altText ?? ''} className="w-full h-full object-cover"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
                          {on && <span className="absolute top-1 right-1 w-6 h-6 rounded-full bg-violet-600 text-white text-xs flex items-center justify-center">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-end gap-3">
                <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*,application/pdf" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="text-xs text-violet-700 hover:underline disabled:opacity-50">{uploading ? 'Uploading…' : '⬆ Upload file'}</button>
                <button onClick={() => setThreadMediaPickerFor(null)} className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-sm font-medium">Done</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add via URL modal */}
      {showAddUrl && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-6 bg-stone-900/40" onClick={() => setShowAddUrl(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-stone-900">Add image by URL</h2>
              <button onClick={() => setShowAddUrl(false)} className="text-stone-400 hover:text-stone-700">✕</button>
            </div>
            <p className="text-xs text-stone-500">Paste any image URL. It gets saved to your media library and attached to <strong>{active}</strong>.</p>
            <input value={addUrlValue} onChange={(e) => setAddUrlValue(e.target.value)} placeholder="https://…/image.jpg" autoFocus
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAddUrl(false)} className="px-3 py-1.5 text-sm text-stone-600 hover:underline">Cancel</button>
              <button onClick={handleAddUrl} className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-sm font-medium">Attach</button>
            </div>
          </div>
        </div>
      )}

      {/* Save-as-template modal */}
      {showTemplate && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-6 bg-stone-900/40" onClick={() => setShowTemplate(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-stone-900">Save as template</h2>
              <button onClick={() => setShowTemplate(false)} className="text-stone-400 hover:text-stone-700">✕</button>
            </div>
            <p className="text-xs text-stone-500">Saves the <strong>{active}</strong> caption for reuse. Variables like <code className="bg-stone-100 px-1 rounded">{'{{name}}'}</code> are auto-detected.</p>
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Name</span>
              <input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Product launch announcement"
                className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Category</span>
              <select value={tplCategory} onChange={(e) => setTplCategory(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                {TEMPLATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowTemplate(false)} className="px-3 py-1.5 text-sm text-stone-600 hover:underline">Cancel</button>
              <button onClick={saveAsTemplate} className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-sm font-medium">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Source picker — compose from / change the source content item. Lists the
          project's KEPT library items; click one to seed the shared caption. */}
      {showSourcePicker && (() => {
        const items: any[] = sourceItemsQ.data?.contentItems ?? [];
        const q = sourceSearch.trim().toLowerCase();
        const filtered = q ? items.filter((it) => (it.title ?? '').toLowerCase().includes(q)) : items;
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center p-6 bg-stone-900/40" onClick={() => setShowSourcePicker(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-stone-900">Compose from a library item</h2>
                <button onClick={() => setShowSourcePicker(false)} className="text-stone-400 hover:text-stone-700">✕</button>
              </div>
              <p className="text-xs text-stone-500 mb-3">Pick a kept content item to seed your caption (title, summary, source URL). You can edit the caption afterwards.</p>
              <div className="relative mb-3">
                <input value={sourceSearch} onChange={(e) => setSourceSearch(e.target.value)} placeholder="Filter by title…" autoFocus
                  className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                <span className="absolute left-3 top-2 text-stone-400">⌕</span>
              </div>
              <div className="flex-1 overflow-y-auto -mx-2 px-2">
                {sourceItemsQ.fetching ? (
                  <p className="text-stone-500 text-sm">Loading…</p>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-12 text-stone-400 text-sm">
                    {items.length === 0
                      ? <>No kept items yet. Keep items in the <NavLink to="/app/content" className="text-violet-600 underline">Content library</NavLink>.</>
                      : 'No items match that filter.'}
                  </div>
                ) : (
                  <ul className="divide-y divide-stone-100">
                    {filtered.map((it) => (
                      <li key={it.id}>
                        <button
                          onClick={() => { attachSource({ contentItemId: it.id, title: it.title, summary: it.summary, url: it.url }); setShowSourcePicker(false); }}
                          className={`w-full text-left px-2 py-2.5 rounded-lg hover:bg-violet-50 ${sourceItemId === it.id ? 'bg-violet-50 ring-1 ring-violet-200' : ''}`}>
                          <p className="text-sm font-medium text-stone-800 line-clamp-1">{it.title || <span className="text-stone-400">(untitled)</span>}</p>
                          <p className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                            {it.sourceLabel && <span>{it.sourceLabel}</span>}
                            {typeof it.relevance === 'number' && <><span>·</span><span>relevance {it.relevance}</span></>}
                            {(it.publishedAt || it.createdAt) && <><span>·</span><span>{new Date(it.publishedAt || it.createdAt).toLocaleDateString()}</span></>}
                            {sourceItemId === it.id && <><span>·</span><span className="text-violet-600 font-medium">current</span></>}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-end">
                <button onClick={() => setShowSourcePicker(false)} className="px-4 py-1.5 text-sm text-stone-600 hover:underline">Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
